import { generateTerraform } from '../terraform/generate';

function sanitize(name) {
  return name.replace(/[^a-zA-Z0-9_-]/g, '_').replace(/^[^a-zA-Z]/, 'r_$&') || 'unnamed';
}

function escapeHcl(str) {
  return str.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

export function generateModuleWrapper(nodes, edges, moduleName) {
  const modName = moduleName || 'landing_zone';
  const result = generateTerraform(nodes, edges);
  if (!result.ok) {
    return { ok: false, errors: result.errors };
  }

  const rgNodes = nodes.filter((n) => n.data?.type === 'resource-group');
  const vnetNodes = nodes.filter((n) => n.data?.type === 'vnet');
  const subnetNodes = nodes.filter((n) => n.data?.type === 'subnet');
  const vmNodes = nodes.filter((n) => n.data?.type === 'virtual-machine');

  const files = [];

  files.push({
    path: `modules/${modName}/main.tf`,
    content: result.hcl,
  });

  // modules/landing_zone/variables.tf
  const varLines = [];
  varLines.push('variable "location" {');
  varLines.push('  type    = string');
  varLines.push('  default = "eastus"');
  varLines.push('  description = "Azure region for all resources"');
  varLines.push('}');
  varLines.push('');
  varLines.push('variable "name_prefix" {');
  varLines.push('  type    = string');
  varLines.push('  default = ""');
  varLines.push('  description = "Prefix for resource names"');
  varLines.push('}');
  varLines.push('');
  varLines.push('variable "tags" {');
  varLines.push('  type    = map(string)');
  varLines.push('  default = {}');
  varLines.push('  description = "Tags applied to all resources"');
  varLines.push('}');
  varLines.push('');

  if (rgNodes.length > 0) {
    varLines.push('variable "resource_groups" {');
    varLines.push('  type = map(object({');
    varLines.push('    name     = string');
    varLines.push('    location = string');
    varLines.push('  }))');
    varLines.push('  description = "Map of resource groups to create"');
    varLines.push('}');
    varLines.push('');
  }

  if (vnetNodes.length > 0) {
    varLines.push('variable "vnets" {');
    varLines.push('  type = map(object({');
    varLines.push('    name           = string');
    varLines.push('    cidr           = string');
    varLines.push('    location       = string');
    varLines.push('    resource_group = string');
    varLines.push('  }))');
    varLines.push('  description = "Map of virtual networks to create"');
    varLines.push('}');
    varLines.push('');
  }

  if (subnetNodes.length > 0) {
    varLines.push('variable "subnets" {');
    varLines.push('  type = map(object({');
    varLines.push('    name           = string');
    varLines.push('    cidr           = string');
    varLines.push('    vnet           = string');
    varLines.push('    resource_group = string');
    varLines.push('  }))');
    varLines.push('  description = "Map of subnets to create"');
    varLines.push('}');
    varLines.push('');
  }

  if (vmNodes.length > 0) {
    varLines.push('variable "virtual_machines" {');
    varLines.push('  type = map(object({');
    varLines.push('    name           = string');
    varLines.push('    size           = string');
    varLines.push('    resource_group = string');
    varLines.push('    subnet         = string');
    varLines.push('  }))');
    varLines.push('  description = "Map of virtual machines to create"');
    varLines.push('}');
    varLines.push('');
  }

  files.push({
    path: `modules/${modName}/variables.tf`,
    content: varLines.join('\n'),
  });

  // modules/landing_zone/outputs.tf
  const outLines = [];
  outLines.push('output "resource_group_names" {');
  outLines.push('  value = {');
  for (const rg of rgNodes) {
    const n = sanitize(rg.data.name);
    outLines.push(`    ${n} = azurerm_resource_group.${n}.name`);
  }
  outLines.push('  }');
  outLines.push('  description = "Names of created resource groups"');
  outLines.push('}');
  outLines.push('');

  if (vnetNodes.length > 0) {
    outLines.push('output "vnet_ids" {');
    outLines.push('  value = {');
    for (const vnet of vnetNodes) {
      const n = sanitize(vnet.data.name);
      outLines.push(`    ${n} = azurerm_virtual_network.${n}.id`);
    }
    outLines.push('  }');
    outLines.push('  description = "IDs of created virtual networks"');
    outLines.push('}');
    outLines.push('');
  }

  if (subnetNodes.length > 0) {
    outLines.push('output "subnet_ids" {');
    outLines.push('  value = {');
    for (const snet of subnetNodes) {
      const n = sanitize(snet.data.name);
      outLines.push(`    ${n} = azurerm_subnet.${n}.id`);
    }
    outLines.push('  }');
    outLines.push('  description = "IDs of created subnets"');
    outLines.push('}');
    outLines.push('');
  }

  if (vmNodes.length > 0) {
    outLines.push('output "vm_ids" {');
    outLines.push('  value = {');
    for (const vm of vmNodes) {
      const n = sanitize(vm.data.name);
      outLines.push(`    ${n} = azurerm_linux_virtual_machine.${n}.id`);
    }
    outLines.push('  }');
    outLines.push('  description = "IDs of created virtual machines"');
    outLines.push('}');
    outLines.push('');
  }

  files.push({
    path: `modules/${modName}/outputs.tf`,
    content: outLines.join('\n'),
  });

  // Root main.tf — module call
  const rootLines = [];
  rootLines.push(`module "${modName}" {`);
  rootLines.push(`  source = "./modules/${modName}"`);
  rootLines.push('');
  rootLines.push('  location = var.location');
  rootLines.push('  tags     = var.tags');
  rootLines.push('');

  if (rgNodes.length > 0) {
    rootLines.push('  resource_groups = {');
    for (const rg of rgNodes) {
      rootLines.push(`    ${sanitize(rg.data.name)} = {`);
      rootLines.push(`      name     = "${escapeHcl(rg.data.name)}"`);
      rootLines.push(`      location = "${escapeHcl(rg.data.region || 'eastus')}"`);
      rootLines.push('    }');
    }
    rootLines.push('  }');
    rootLines.push('');
  }

  if (vnetNodes.length > 0) {
    rootLines.push('  vnets = {');
    for (const vnet of vnetNodes) {
      rootLines.push(`    ${sanitize(vnet.data.name)} = {`);
      rootLines.push(`      name           = "${escapeHcl(vnet.data.name)}"`);
      rootLines.push(`      cidr           = "${escapeHcl(vnet.data.cidr || '10.0.0.0/16')}"`);
      rootLines.push(`      location       = "${escapeHcl(vnet.data.region || 'eastus')}"`);
      rootLines.push(`      resource_group = "${escapeHcl(vnet.data.resourceGroupName || rgNodes[0]?.data.name || '')}"`);
      rootLines.push('    }');
    }
    rootLines.push('  }');
    rootLines.push('');
  }

  if (subnetNodes.length > 0) {
    rootLines.push('  subnets = {');
    for (const snet of subnetNodes) {
      rootLines.push(`    ${sanitize(snet.data.name)} = {`);
      rootLines.push(`      name           = "${escapeHcl(snet.data.name)}"`);
      rootLines.push(`      cidr           = "${escapeHcl(snet.data.cidr || '10.0.1.0/24')}"`);
      rootLines.push(`      vnet           = "${escapeHcl(vnetNodes[0]?.data.name || '')}"`);
      rootLines.push(`      resource_group = "${escapeHcl(rgNodes[0]?.data.name || '')}"`);
      rootLines.push('    }');
    }
    rootLines.push('  }');
    rootLines.push('');
  }

  if (vmNodes.length > 0) {
    rootLines.push('  virtual_machines = {');
    for (const vm of vmNodes) {
      rootLines.push(`    ${sanitize(vm.data.name)} = {`);
      rootLines.push(`      name           = "${escapeHcl(vm.data.name)}"`);
      rootLines.push(`      size           = "${escapeHcl(vm.data.sku || 'Standard_B2s')}"`);
      rootLines.push(`      resource_group = "${escapeHcl(rgNodes[0]?.data.name || '')}"`);
      rootLines.push(`      subnet         = "${escapeHcl(subnetNodes[0]?.data.name || '')}"`);
      rootLines.push('    }');
    }
    rootLines.push('  }');
    rootLines.push('');
  }

  rootLines.push('}');
  rootLines.push('');

  // Root variables.tf
  const rootVars = [];
  rootVars.push('variable "location" {');
  rootVars.push('  type    = string');
  rootVars.push('  default = "eastus"');
  rootVars.push('}');
  rootVars.push('');
  rootVars.push('variable "tags" {');
  rootVars.push('  type    = map(string)');
  rootVars.push('  default = {');
  rootVars.push('    managed_by = "comet"');
  rootVars.push('  }');
  rootVars.push('}');
  rootVars.push('');

  // Root outputs.tf
  const rootOuts = [];
  rootOuts.push(`output "landing_zone_resource_groups" {`);
  rootOuts.push(`  value = module.${modName}.resource_group_names`);
  rootOuts.push('}');
  rootOuts.push('');
  if (vnetNodes.length > 0) {
    rootOuts.push(`output "landing_zone_vnet_ids" {`);
    rootOuts.push(`  value = module.${modName}.vnet_ids`);
    rootOuts.push('}');
    rootOuts.push('');
  }
  if (subnetNodes.length > 0) {
    rootOuts.push(`output "landing_zone_subnet_ids" {`);
    rootOuts.push(`  value = module.${modName}.subnet_ids`);
    rootOuts.push('}');
    rootOuts.push('');
  }

  files.push({ path: 'main.tf', content: rootLines.join('\n') });
  files.push({ path: 'variables.tf', content: rootVars.join('\n') });
  files.push({ path: 'outputs.tf', content: rootOuts.join('\n') });

  return { ok: true, files };
}
